import { clsx } from 'clsx';
import type {
  CrossedSourceDocument,
  CrossingDocumentType,
  SourceDocumentsFactBoxProps,
} from './MasterDocument.types';
import { useMasterDocumentTranslation } from './i18n';
import { useMasterDocumentFormatters } from './formatters';
import { inlineActionButtonDanger } from './inlineActions';

function SignBadge({ type }: { type: CrossingDocumentType }) {
  const isEcd = type === 'ECD';
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-xs font-bold',
        isEcd
          ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
          : 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
      )}
    >
      {type} · {isEcd ? '+' : '−'}
    </span>
  );
}

// ─── Badge de estado del padre (POSTED → INVOICED) ─────────
function StateBadge({ state }: { state: string }) {
  const upper = state.toUpperCase();
  const isInvoiced = upper === 'INVOICED';
  return (
    <span
      className={clsx(
        'inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wide',
        isInvoiced
          ? 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300'
          : 'bg-bg-secondary text-content-secondary dark:bg-dark-bg-secondary dark:text-dark-content-secondary',
      )}
    >
      {state}
    </span>
  );
}

function CrossedRow({
  doc,
  editable,
  busy,
  onUncross,
  formatCurrency,
  uncrossLabel,
}: {
  doc: CrossedSourceDocument;
  editable: boolean;
  busy: boolean;
  onUncross?: (id: string) => void;
  formatCurrency: (value: number) => string;
  uncrossLabel: string;
}) {
  return (
    <li className="flex items-start justify-between gap-2 py-2 border-t border-border-primary dark:border-dark-border-primary first:border-t-0">
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <SignBadge type={doc.type} />
          <span className="text-sm font-medium text-content-primary dark:text-dark-content-primary truncate">
            {doc.number}
          </span>
          {doc.state && <StateBadge state={doc.state} />}
        </div>
        <span className="block text-xs text-content-secondary dark:text-dark-content-secondary">
          {doc.classLabel}
        </span>
      </div>
      <div className="flex flex-col items-end gap-1 flex-shrink-0">
        <span className="text-sm font-mono text-content-primary dark:text-dark-content-primary whitespace-nowrap">
          {doc.crossingSign < 0 ? '−' : '+'}
          {formatCurrency(doc.netTotal)}
        </span>
        {editable && onUncross && (
          <button
            type="button"
            onClick={() => onUncross(doc.id)}
            disabled={busy}
            className={inlineActionButtonDanger}
          >
            {uncrossLabel}
          </button>
        )}
      </div>
    </li>
  );
}

/**
 * SourceDocumentsFactBox — widget FactBox "Documentos origen" (Épica 105-5,
 * Story 105-5.04). Lista los documentos padre YA cruzados con su badge de signo
 * (ECD·+/DCD·−), estado (POSTED→INVOICED) y, en modo editable, "Desvincular"
 * por documento. Pensado para registrarse como `children` de un
 * `FactBoxSectionConfig` (ADR-017).
 */
export function SourceDocumentsFactBox({
  documents,
  subtotal,
  editable = false,
  busy = false,
  onUncross,
  formatCurrency: formatCurrencyProp,
  messages,
  className,
}: SourceDocumentsFactBoxProps) {
  const { t } = useMasterDocumentTranslation();
  const { formatCurrency: formatCurrencyDefault } = useMasterDocumentFormatters();
  const formatCurrency = formatCurrencyProp ?? formatCurrencyDefault;
  const emptyCrossed = messages?.emptyCrossed ?? t('master_document.crossing.emptyCrossed');
  const uncrossLabel = messages?.uncross ?? t('master_document.crossing.uncross');
  const subtotalLabel = messages?.subtotalLabel ?? t('master_document.crossing.subtotalLabel');

  if (documents.length === 0) {
    return (
      <p className={clsx('text-sm text-content-secondary dark:text-dark-content-secondary px-1 py-2', className)}>
        {emptyCrossed}
      </p>
    );
  }

  return (
    <div className={className}>
      <ul className="px-1">
        {documents.map((doc) => (
          <CrossedRow
            key={doc.id}
            doc={doc}
            editable={editable}
            busy={busy}
            onUncross={onUncross}
            formatCurrency={formatCurrency}
            uncrossLabel={uncrossLabel}
          />
        ))}
      </ul>
      {subtotal != null && (
        <div className="mt-2 pt-2 px-1 border-t border-border-primary dark:border-dark-border-primary flex items-center justify-between">
          <span className="text-xs text-content-secondary dark:text-dark-content-secondary">{subtotalLabel}</span>
          <span className="text-sm font-bold font-mono text-content-primary dark:text-dark-content-primary">
            {formatCurrency(subtotal)}
          </span>
        </div>
      )}
    </div>
  );
}
